import { selectIsAuth } from '@features/auth/store/auth.selectors';
import { FormOffer } from '@features/offers/screens/FormOffer';
import { OfferExhibition } from '@features/offers/screens/offerExhibition/offerExhibition';
import { FormProduct } from '@features/products/screens/formProduct';
import { ProductExhibition } from '@features/products/screens/productsExhibition/productsExhibition';
import {
  AssetsPage,
  CouponPage,
  CreateCouponDigitalPage,
  CreateCouponPhysicPage,
  EventDetailPage,
  EventsPage,
  LinkAssets,
  LinkCustomers,
  LinkPrograms,
  LoginPage,
  ModalDetailPage,
  ModalPage,
  ModalsOrdering,
  OrderBumpPage,
  SegmentDetailPage,
  SegmentsOrdering,
  SegmentsPage,
} from '@pages';
import { FC, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Navigate, Route, Routes } from 'react-router-dom';

import { AdminScreenDetails } from 'src/features/admin/screens/admin-details-screen/AdminScreenDetails';
import { AdminScreenListing } from 'src/features/admin/screens/admin-list-screen/AdminScreenListing';
import { ClientDetailsScreen } from 'src/features/clients/screens/client-details-screen/ClientDetailsScreen';
import { ClientScreenListing } from 'src/features/clients/screens/client-listing-screen/ClientScreenListing';
import { CustomerDetailsScreen } from 'src/features/customer/screens/CustomerDetailsScreen';
import { DashboardScreen } from 'src/features/dashboard/screens/DashboardScreen';
import { FreezingScreen } from 'src/features/freezing/screens/FreezingScreen';
import { FunnelEditScreen } from 'src/features/Funnel/screens/FunnelEdit/FunnelEditScreen';
import { FunelGestation } from 'src/features/Funnel/screens/FunnelGestation/FunelGestationScreen';
import { FunnelFunctionalityPageScreen } from 'src/features/Funnel/screens/FunnelPage/Functionality/FunctionalityPageScreen';
import { FunnelMediaPageScreen } from 'src/features/Funnel/screens/FunnelPage/MediaPage/MediaPageScreen';
import { FunnelPageOfferPageScreen } from 'src/features/Funnel/screens/FunnelPage/OfferPage/OfferPageScreen';
import { FunnelPageQuestionPageScreen } from 'src/features/Funnel/screens/FunnelPage/QuestionPage/QuestionPageScreen';
import { FunnelReleasePageScreen } from 'src/features/Funnel/screens/FunnelPage/ReleasePage/ReleasePageScreen';
import { FunnelSpecialistCarePageScreen } from 'src/features/Funnel/screens/FunnelPage/SpecialistCarePage/SpecialistCarePageScreen';
import { FunnelVideoPageScreen } from 'src/features/Funnel/screens/FunnelPage/VideoPage/VideoPageScreen';
import { BadgeDetailsScreen } from 'src/features/gamefication/badges/screen/BadgeDetailsScreen';
import { BadgeListScreen } from 'src/features/gamefication/badges/screen/BadgeListScreen';
import { BadgeGroupCreateScreeen } from 'src/features/gamefication/badges-groups/screen/BadgeGroupCreateScreeen';
import { BadgeGroupDetailsScreen } from 'src/features/gamefication/badges-groups/screen/BadgeGroupDetailsScreen';
import { BadgeGroupListScreen } from 'src/features/gamefication/badges-groups/screen/BadgeGroupListScreen';
import {
  GameficationmanageCompetitionForm,
  GameficationManageCompetitionList,
} from 'src/features/gamefication/competitions/Index';
import { SocialDashboard } from 'src/features/gamefication/dashboard/SocialDashboard';
import { HomeScreen } from 'src/features/home/screens/HomeScreen';
import { ErrorScreen } from 'src/features/layout/screens/ErrorScreen';
import { LinkGenerator } from 'src/features/linkManager/screens/LinkGenerator';
import { LinkShortner } from 'src/features/linkManager/screens/LinkShortner';
import { ModalRedirect } from 'src/features/linkManager/screens/ModalRedirect';
import { PermissionsScreen } from 'src/features/permissions/screens/PermissionsScreen';
import { CreateOrUpdateProgramsV2Screen } from 'src/features/programsV2/screens/CreateOrUpdateProgramsV2Screen';
import { ProgramsV2Screen } from 'src/features/programsV2/screens/ProgramsV2Screen';
import { RoleScreen } from 'src/features/roles/screen/RoleScreen';
import { SubscriptionListScreen } from 'src/features/subscriptions/scheduled-reports/Index';
import { TenantForm } from 'src/features/tenants/screens/TenantForm';
import { TenantList } from 'src/features/tenants/screens/TenantList';
import { UpsellFunnel } from 'src/features/upsellFunnel/screens/UpsellFunnel/UpsellFunnel.page';
import { UpsellStepFlow } from 'src/features/upsellFunnel/screens/UpsellStepFlow/UpsellStepFlow.page';

import { CreateNewCustomerScreen } from '../features/customer/screens/CreateNewCustomerScreen';
import { NotFoundRouteScreen } from '../features/layout/screens/NotFoundRouteScreen';
import { LgpdPage } from '../features/Lgpd/screens/LgpdScreen';
import { BannerManagementScreen } from '../features/marketplace/screens/BannerManagementScreen';
import { BannerOrderingScreen } from '../features/marketplace/screens/BannerOrderingScreen';
import { CreateOrUpdateBannerScreen } from '../features/marketplace/screens/CreateOrUpdateBannerScreen';
import { CreateOrUpdateMarketplaceScreen } from '../features/marketplace/screens/CreateOrUpdateMarketplaceScreen';
import { MarketplaceScreen } from '../features/marketplace/screens/MarketplaceScreen';
import { RedirectProgramsToV2 } from '../features/programsV2/components/redirectToProgramsV2';
import { GuestRoute } from './components/GuestRoute';
import { ProtectedRoute } from './components/ProtectedRoute';
import { EditCouponDigitalPage } from './framework/pages/Coupons/digital/EditCouponDigital.page';
import { ReadCouponDigitalPage } from './framework/pages/Coupons/digital/ReadCouponDigital.page';
import { EditCouponPhysicPage } from './framework/pages/Coupons/physic/EditCouponPhysic.page';
import { ReadCouponPhysicPage } from './framework/pages/Coupons/physic/ReadCouponPhysic.page';
import { CreateOrderBump } from './framework/pages/OrderBumps/CreateOrderBump.page';

export const AppRoutes: FC = () => {
  const isAuth = useSelector(selectIsAuth);
  const [redirect, setRedirect] = useState<string>('/home');

  useEffect(() => {
    if (isAuth) {
      setRedirect('/login');
    }
  }, [isAuth]);

  return (
    <Routes>
      <Route path='/' element={<Navigate to={redirect} replace />} />

      <Route
        path='/login'
        element={
          <GuestRoute>
            <LoginPage />
          </GuestRoute>
        }
      />

      <Route
        path='/home'
        element={
          <ProtectedRoute>
            <HomeScreen />
          </ProtectedRoute>
        }
      />
      <Route
        path='/dashboard'
        element={
          <ProtectedRoute>
            <DashboardScreen />
          </ProtectedRoute>
        }
      />
      <Route
        path='/admin'
        element={
          <ProtectedRoute>
            <AdminScreenListing />
          </ProtectedRoute>
        }
      />
      <Route
        path='/admin/:id'
        element={
          <ProtectedRoute>
            <AdminScreenDetails />
          </ProtectedRoute>
        }
      />
      <Route
        path='/clientes'
        element={
          <ProtectedRoute>
            <ClientScreenListing />
          </ProtectedRoute>
        }
      />
      <Route
        path='/cliente/:id'
        element={
          <ProtectedRoute>
            <CustomerDetailsScreen />
          </ProtectedRoute>
        }
      />
      <Route
        path='/clientes/novo'
        element={
          <ProtectedRoute>
            <CreateNewCustomerScreen />
          </ProtectedRoute>
        }
      />
      <Route
        path='/cliente/old/:id'
        element={
          <ProtectedRoute>
            <ClientDetailsScreen />
          </ProtectedRoute>
        }
      />

      <Route
        path='/programs/create-or-update/:id?'
        element={<RedirectProgramsToV2 />}
      />
      <Route
        path='/programs/*'
        element={<Navigate to='/programs/v2' replace />}
      />

      <Route
        path='/programs/v2'
        element={
          <ProtectedRoute>
            <ProgramsV2Screen />
          </ProtectedRoute>
        }
      />

      <Route
        path='/programs/v2/create-or-update/:id?'
        element={
          <ProtectedRoute>
            <CreateOrUpdateProgramsV2Screen />
          </ProtectedRoute>
        }
      />

      <Route
        path='/permissao'
        element={
          <ProtectedRoute>
            <RoleScreen />
          </ProtectedRoute>
        }
      />
      <Route
        path='/permissao/:id'
        element={
          <ProtectedRoute>
            <RoleScreen />
          </ProtectedRoute>
        }
      />

      <Route
        path='/permissions'
        element={
          <ProtectedRoute>
            <PermissionsScreen />
          </ProtectedRoute>
        }
      />

      <Route
        path='/unsubscribe/funnel'
        element={
          <ProtectedRoute>
            <FunelGestation />
          </ProtectedRoute>
        }
      />
      <Route
        path='/upsell/funnel'
        element={
          <ProtectedRoute>
            <UpsellFunnel />
          </ProtectedRoute>
        }
      />
      <Route
        path='/upsell/step/flow'
        element={
          <ProtectedRoute>
            <UpsellStepFlow />
          </ProtectedRoute>
        }
      />
      <Route
        path='/unsubscribe/funnel/:id'
        element={
          <ProtectedRoute>
            <FunnelEditScreen />
          </ProtectedRoute>
        }
      />
      <Route
        path={'/unsubscribe/funnel/question/:id'}
        element={
          <ProtectedRoute>
            <FunnelPageQuestionPageScreen />
          </ProtectedRoute>
        }
      />
      <Route
        path={'/unsubscribe/funnel/specialist-care/:id'}
        element={
          <ProtectedRoute>
            <FunnelSpecialistCarePageScreen />
          </ProtectedRoute>
        }
      />
      <Route
        path={'/unsubscribe/funnel/release/:id'}
        element={
          <ProtectedRoute>
            <FunnelReleasePageScreen />
          </ProtectedRoute>
        }
      />
      <Route
        path={'/unsubscribe/funnel/video/:id'}
        element={
          <ProtectedRoute>
            <FunnelVideoPageScreen />
          </ProtectedRoute>
        }
      />
      <Route
        path={'/unsubscribe/funnel/media/:id'}
        element={
          <ProtectedRoute>
            <FunnelMediaPageScreen />
          </ProtectedRoute>
        }
      />
      <Route
        path={'/unsubscribe/funnel/functionality/:id'}
        element={
          <ProtectedRoute>
            <FunnelFunctionalityPageScreen />
          </ProtectedRoute>
        }
      />
      <Route
        path={'/unsubscribe/funnel/offer/:id'}
        element={
          <ProtectedRoute>
            <FunnelPageOfferPageScreen />
          </ProtectedRoute>
        }
      />

      <Route
        path='/assinaturas/cancelamentos/agendas'
        element={
          <ProtectedRoute>
            <SubscriptionListScreen />
          </ProtectedRoute>
        }
      />

      <Route
        path='/screens/Assets'
        element={
          <ProtectedRoute>
            <AssetsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path='/screens/segments/manage'
        element={
          <ProtectedRoute>
            <SegmentsPage />
          </ProtectedRoute>
        }
      />

      <Route
        path='/screens/segments/detalhes'
        element={
          <ProtectedRoute>
            <SegmentDetailPage />
          </ProtectedRoute>
        }
      />

      <Route
        path='/screens/segments/link/assets'
        element={
          <ProtectedRoute>
            <LinkAssets />
          </ProtectedRoute>
        }
      />

      <Route
        path='/screens/segments/link/customers'
        element={
          <ProtectedRoute>
            <LinkCustomers />
          </ProtectedRoute>
        }
      />
      <Route
        path='/screens/segments/link/programs'
        element={
          <ProtectedRoute>
            <LinkPrograms />
          </ProtectedRoute>
        }
      />
      <Route
        path='/screens/segments/ordering'
        element={
          <ProtectedRoute>
            <SegmentsOrdering />
          </ProtectedRoute>
        }
      />

      <Route
        path='/social/dashboard'
        element={
          <ProtectedRoute>
            <SocialDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path='/social/gamefication/events'
        element={
          <ProtectedRoute>
            <EventsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path='/social/gamefication/events/edit'
        element={
          <ProtectedRoute>
            <EventDetailPage />
          </ProtectedRoute>
        }
      />
      <Route
        path='/social/gamefication/badges/groups'
        element={
          <ProtectedRoute>
            <BadgeGroupListScreen />
          </ProtectedRoute>
        }
      />
      <Route
        path='/social/gamefication/badges/groups/create'
        element={
          <ProtectedRoute>
            <BadgeGroupCreateScreeen />
          </ProtectedRoute>
        }
      />
      <Route
        path='/social/gamefication/badges/groups/:id'
        element={
          <ProtectedRoute>
            <BadgeGroupDetailsScreen />
          </ProtectedRoute>
        }
      />
      <Route
        path='/social/gamefication/badges'
        element={
          <ProtectedRoute>
            <BadgeListScreen />
          </ProtectedRoute>
        }
      />
      <Route
        path='/social/gamefication/badges/:id'
        element={
          <ProtectedRoute>
            <BadgeDetailsScreen />
          </ProtectedRoute>
        }
      />

      <Route
        path='/social/gamefication/competitions'
        element={
          <ProtectedRoute>
            <GameficationManageCompetitionList />
          </ProtectedRoute>
        }
      />

      <Route
        path='/social/gamefication/competition/create'
        element={
          <ProtectedRoute>
            <GameficationmanageCompetitionForm />
          </ProtectedRoute>
        }
      />

      <Route
        path='/social/gamefication/competition/:id'
        element={
          <ProtectedRoute>
            <GameficationmanageCompetitionForm />
          </ProtectedRoute>
        }
      />

      <Route
        path='/Lgpd'
        element={
          <ProtectedRoute>
            <LgpdPage />
          </ProtectedRoute>
        }
      />
      <Route
        path='/freezing'
        element={
          <ProtectedRoute>
            <FreezingScreen />
          </ProtectedRoute>
        }
      />

      {/* All Product Pages */}
      <Route path='/products'>
        <Route
          path=''
          element={
            <ProtectedRoute>
              <ProductExhibition />
            </ProtectedRoute>
          }
        />
        <Route
          path='create'
          element={
            <ProtectedRoute>
              <FormProduct />
            </ProtectedRoute>
          }
        />
        <Route
          path=':productId/edit'
          element={
            <ProtectedRoute>
              <FormProduct />
            </ProtectedRoute>
          }
        />
        <Route
          path=':productId/read'
          element={
            <ProtectedRoute>
              <FormProduct />
            </ProtectedRoute>
          }
        />
        <Route path=':productId/offers'>
          <Route
            path=''
            element={
              <ProtectedRoute>
                <OfferExhibition />
              </ProtectedRoute>
            }
          />
          <Route
            path='create'
            element={
              <ProtectedRoute>
                <FormOffer />
              </ProtectedRoute>
            }
          />
          <Route
            path=':offerId'
            element={
              <ProtectedRoute>
                <FormOffer />
              </ProtectedRoute>
            }
          />
        </Route>
      </Route>

      <Route
        path='/screens/modals/detail'
        element={
          <ProtectedRoute>
            <ModalDetailPage />
          </ProtectedRoute>
        }
      />

      <Route
        path='/screens/modals'
        element={
          <ProtectedRoute>
            <ModalPage />
          </ProtectedRoute>
        }
      />

      <Route
        path='/screens/modals/ordering'
        element={
          <ProtectedRoute>
            <ModalsOrdering />
          </ProtectedRoute>
        }
      />

      {/* All Coupons Pages */}
      <Route
        path='/screens/coupons'
        element={
          <ProtectedRoute>
            <CouponPage />
          </ProtectedRoute>
        }
      />

      {/* Physic Coupons Pages */}
      <Route
        path='/screens/physic/coupons/create'
        element={
          <ProtectedRoute>
            <CreateCouponPhysicPage />
          </ProtectedRoute>
        }
      />
      <Route
        path='/screens/physic/coupons/read'
        element={
          <ProtectedRoute>
            <ReadCouponPhysicPage />
          </ProtectedRoute>
        }
      />
      <Route
        path='/screens/physic/coupons/:id'
        element={
          <ProtectedRoute>
            <EditCouponPhysicPage />
          </ProtectedRoute>
        }
      />

      {/* Digital Coupons Pages */}
      <Route
        path='/screens/digital/coupons/create'
        element={
          <ProtectedRoute>
            <CreateCouponDigitalPage />
          </ProtectedRoute>
        }
      />
      <Route
        path='/screens/digital/coupons/read'
        element={
          <ProtectedRoute>
            <ReadCouponDigitalPage />
          </ProtectedRoute>
        }
      />
      <Route
        path='/screens/digital/coupons/:id'
        element={
          <ProtectedRoute>
            <EditCouponDigitalPage />
          </ProtectedRoute>
        }
      />

      {/* Order Bump Pages */}
      <Route
        path='/screens/order-bump'
        element={
          <ProtectedRoute>
            <OrderBumpPage />
          </ProtectedRoute>
        }
      />

      <Route
        path='/screens/order-bump/create'
        element={
          <ProtectedRoute>
            <CreateOrderBump />
          </ProtectedRoute>
        }
      />
      <Route
        path='/link-generator'
        element={
          <ProtectedRoute>
            <LinkGenerator />
          </ProtectedRoute>
        }
      />

      <Route
        path='/link-shortner'
        element={
          <ProtectedRoute>
            <LinkShortner />
          </ProtectedRoute>
        }
      />

      <Route
        path='/modal-redirect'
        element={
          <ProtectedRoute>
            <ModalRedirect />
          </ProtectedRoute>
        }
      />

      {/*tenant pages */}
      <Route
        path='/tenants/create'
        element={
          <ProtectedRoute requirePermissionMaster>
            <TenantForm />
          </ProtectedRoute>
        }
      />
      <Route
        path='/tenants/:id'
        element={
          <ProtectedRoute requirePermissionMaster>
            <TenantForm />
          </ProtectedRoute>
        }
      />
      <Route
        path='/tenants'
        element={
          <ProtectedRoute requirePermissionMaster>
            <TenantList />
          </ProtectedRoute>
        }
      />

      <Route
        path='/marketplace'
        element={
          <ProtectedRoute>
            <MarketplaceScreen />
          </ProtectedRoute>
        }
      />

      <Route
        path='/marketplace/create-or-update/:id?'
        element={
          <ProtectedRoute>
            <CreateOrUpdateMarketplaceScreen />
          </ProtectedRoute>
        }
      />

      <Route
        path='/marketplace/banners/gerenciamento'
        element={
          <ProtectedRoute>
            <BannerManagementScreen />
          </ProtectedRoute>
        }
      />

      <Route
        path='/marketplace/banners/create-or-update/:id?'
        element={
          <ProtectedRoute>
            <CreateOrUpdateBannerScreen />
          </ProtectedRoute>
        }
      />

      <Route
        path='/marketplace/banners/ordenacao'
        element={
          <ProtectedRoute>
            <BannerOrderingScreen />
          </ProtectedRoute>
        }
      />

      <Route path='/erro' element={<ErrorScreen />} />

      <Route path='*' element={<NotFoundRouteScreen />} />
    </Routes>
  );
};
